import { Grid, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { AssistantMessage } from '../../../shared/types/assistant';
import logoRounded from '../../../assets/images/logos/logo-victoria.png';

interface Props {
    messages: AssistantMessage[];
    onClickDownload: (contentItem: any) => void;
}

interface MessageBubbleProps {
    role?: 'user' | 'assistant';
    text: string | React.ReactNode;
    time?: string;
}

export function MessageBubble({
    role = 'user',
    text,
    time = '',
}: MessageBubbleProps) {
    const isUser = role === 'user';

    return (
        <div
            className={`chat-bubble ${
                isUser ? 'chat-bubble--user' : 'chat-bubble--assistant'
            }`}
        >
            {!isUser && (
                <img src={logoRounded} className="chat-bubble__avatar" />
            )}

            <div
                className={`chat-bubble__content ${
                    isUser
                        ? 'chat-bubble__content--user'
                        : 'chat-bubble__content--assistant'
                }`}
            >
                <p className="chat-bubble__text">{text}</p>

                {time && (
                    <div
                        className={`chat-bubble__time ${
                            isUser
                                ? 'chat-bubble__time--user'
                                : 'chat-bubble__time--assistant'
                        }`}
                    >
                        {time}
                    </div>
                )}
            </div>

            {isUser && <div className="chat-bubble__user-tag">Tú</div>}
        </div>
    );
}

const renderTextWithFiles = (
    text: string,
    fileOutputs: { id?: string; name: string; url?: string }[],
    onClickDownload: (contentItem: any) => void
) => {
    const parts: any[] = [];
    let lastIndex = 0;

    const regex =
        /\[([^\]]+)\]\((file_id:|attachmentId:|https?:\/\/)([^\)]+)\)|(https?:\/\/[^\s]+)/g;

    let match;

    while ((match = regex.exec(text)) !== null) {
        const fullMatch = match[0];
        const linkText = match[1];
        const prefix = match[2];
        const value = match[3];

        if (match.index > lastIndex) {
            parts.push({ text: text.slice(lastIndex, match.index) });
        }

        if (prefix === 'file_id:' || prefix === 'attachmentId:') {
            const file = fileOutputs.find((f) => f.id === value);
            if (file) parts.push({ id: value, name: linkText });
        } else {
            parts.push({ url: fullMatch.trim(), name: 'Descargar archivo' });
        }

        lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < text.length) {
        parts.push({ text: text.slice(lastIndex) });
    }

    return parts.map((part, idx) => {
        if (part.text) {
            return (
                <span key={idx} className="chat-bubble__inline-text">
                    {part.text}
                </span>
            );
        }

        return (
            <Button
                key={idx}
                onClick={() => {
                    if (part.url) {
                        const a = document.createElement('a');
                        a.href = part.url;
                        a.download = '';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        return;
                    }
                    onClickDownload({ id: part.id, name: part.name });
                }}
                startIcon={<DownloadIcon />}
                className="chat-bubble__file-btn"
            >
                {part.name}
            </Button>
        );
    });
};

export const ShowMessagesAndFiles = ({ messages, onClickDownload }: Props) => {
    return (
        <>
            {messages?.map((message, msgIdx) => (
                <Grid item xs={12} key={`message-${msgIdx}`}>
                    {message?.content?.map((contentItem, idx) => {
                        if (contentItem.type !== 'text') return null;

                        const textElements = renderTextWithFiles(
                            contentItem.text?.value || '',
                            (contentItem.fileOutputs || []) as any,
                            onClickDownload
                        );

                        return (
                            <MessageBubble
                                key={`bubble-${msgIdx}-${idx}`}
                                role={message.isGpt ? 'assistant' : 'user'}
                                text={
                                    <div className="chat-bubble__inline">
                                        {textElements}
                                    </div>
                                }
                                time={message.time || ''}
                            />
                        );
                    })}
                </Grid>
            ))}
        </>
    );
};
